import React, { useState } from 'react';
import { Card, Button, Dropdown } from 'react-bootstrap';
import { FaTimes, FaCheck } from 'react-icons/fa';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { rowsToCsv, downloadCsv } from '../../utils/csvExport';

const TYPE_LABEL = { markdown: 'Documento', html: 'Vista HTML', texto: 'Texto', tabla: 'Tabla' };
const EXT = { markdown: 'md', html: 'html', texto: 'txt', tabla: 'csv' };

const slug = (s) =>
  (s || 'documento')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'documento';

// El markdown se sanitiza (DOMPurify) antes de inyectarlo: el output del modelo es no confiable.
const renderMarkdown = (md) => DOMPurify.sanitize(marked.parse(md || ''));

const escapeHtml = (s) =>
  (s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Parsea el content de un artifact 'tabla' ({columnas, filas}) de forma defensiva:
// el modelo puede mandar basura, no debe romper el panel.
function parseTabla(artifact) {
  try {
    const data = JSON.parse(artifact.content);
    const columnas = Array.isArray(data.columnas) ? data.columnas.map((c) => String(c ?? '')) : [];
    if (columnas.length === 0) return null;
    const filas = Array.isArray(data.filas)
      ? data.filas.map((f) => {
          const row = (Array.isArray(f) ? f : [f]).map((c) => String(c ?? ''));
          // Normaliza cada fila al largo de columnas (rellena/corta).
          while (row.length < columnas.length) row.push('');
          return row.slice(0, columnas.length);
        })
      : [];
    return { columnas, filas };
  } catch {
    return null;
  }
}

const norm = (v) =>
  String(v ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const STATUS_COLS = ['estado', 'vencimiento', 'situacion'];
const isStatusCol = (name) => STATUS_COLS.some((s) => norm(name) === s || norm(name).includes(s));

// Coloración determinística de un valor de estado (la decide el panel, no el modelo).
function statusBadge(value) {
  const v = norm(value);
  if (['vigente', 'vigentes', 'activa', 'activo', 'al dia', 'ok'].includes(v)) {
    return { background: '#d1e7dd', color: '#0f5132' };
  }
  if (['vencido', 'vencida', 'baja', 'inactivo', 'finalizada'].includes(v)) {
    return { background: '#f8d7da', color: '#842029' };
  }
  if (['por vencer', 'proximo', 'critico', 'advertencia'].includes(v)) {
    return { background: '#fff3cd', color: '#664d03' };
  }
  return null;
}

const thStyle = {
  background: '#f8f9fa',
  color: '#495057',
  fontWeight: 600,
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: '2px solid #dee2e6',
  position: 'sticky',
  top: 0,
  whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '6px 10px',
  borderBottom: '1px solid #dee2e6',
  textAlign: 'left',
  verticalAlign: 'top',
};
const badgeStyle = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

function ArtifactTable({ data }) {
  if (!data || data.filas.length === 0) {
    return <div className="text-muted text-center p-4">Esta tabla vino vacía.</div>;
  }
  const { columnas, filas } = data;
  const statusIdx = columnas.map((c, i) => (isStatusCol(c) ? i : -1)).filter((i) => i >= 0);
  return (
    <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '13px',
          color: '#212529',
        }}
      >
        <thead>
          <tr>
            {columnas.map((c, i) => (
              <th key={i} style={thStyle}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
              {row.map((cell, ci) => {
                const badge = statusIdx.includes(ci) ? statusBadge(cell) : null;
                return (
                  <td key={ci} style={tdStyle}>
                    {badge ? <span style={{ ...badgeStyle, ...badge }}>{cell}</span> : cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ArtifactBody({ artifact, tabla }) {
  if (artifact.type === 'tabla') {
    return <ArtifactTable data={tabla} />;
  }
  if (!artifact.content) {
    return <div className="text-muted text-center p-4">Este contenido vino vacío.</div>;
  }
  if (artifact.type === 'html') {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className="text-muted small px-3 py-1">Vista previa · sin scripts</div>
        <iframe
          title={artifact.title}
          srcDoc={artifact.content}
          sandbox=""
          style={{ flex: 1, width: '100%', border: 0, background: '#fff' }}
        />
      </div>
    );
  }
  if (artifact.type === 'texto') {
    return (
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'ui-monospace, monospace',
          fontSize: '13px',
          padding: '16px',
          margin: 0,
        }}
      >
        {artifact.content}
      </pre>
    );
  }
  // markdown
  return (
    <div
      className="artifact-markdown"
      style={{ padding: '16px 20px', maxWidth: '760px' }}
      dangerouslySetInnerHTML={{ __html: renderMarkdown(artifact.content) }}
    />
  );
}

function ArtifactPanel({ artifact, allArtifacts = [], onSelect, onClose, onCopied }) {
  const [copied, setCopied] = useState(false);
  if (!artifact) return null;

  const isTabla = artifact.type === 'tabla';
  const tabla = isTabla ? parseTabla(artifact) : null;

  const copy = async () => {
    try {
      // Para tabla copiamos TSV: se pega directo como tabla en Excel/Sheets/Word.
      const text =
        isTabla && tabla
          ? [tabla.columnas.join('\t'), ...tabla.filas.map((r) => r.join('\t'))].join('\n')
          : artifact.content;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (onCopied) onCopied();
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // sin permiso de portapapeles: no rompemos
    }
  };

  const download = () => {
    if (isTabla && tabla) {
      downloadCsv(rowsToCsv(tabla.columnas, tabla.filas), `${slug(artifact.title)}.csv`);
      return;
    }
    const blob = new Blob([artifact.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug(artifact.title)}.${EXT[artifact.type] || 'txt'}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Imprime/exporta a PDF: renderiza el contenido (sanitizado) en una ventana y abre el diálogo.
  const print = () => {
    let inner;
    if (isTabla && tabla) {
      const head = tabla.columnas.map((c) => `<th>${escapeHtml(c)}</th>`).join('');
      const body = tabla.filas
        .map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`)
        .join('');
      inner = `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    } else if (artifact.type === 'markdown') {
      inner = renderMarkdown(artifact.content);
    } else if (artifact.type === 'html') {
      inner = DOMPurify.sanitize(artifact.content);
    } else {
      inner = `<pre style="white-space:pre-wrap;font-family:ui-monospace,monospace">${escapeHtml(artifact.content)}</pre>`;
    }
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(
      `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>${escapeHtml(
        artifact.title || 'documento',
      )}</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:24px;max-width:820px;margin:0 auto;color:#111;line-height:1.5}h1,h2,h3{margin:0 0 8px}table{border-collapse:collapse;width:100%}th{background:#f8f9fa}td,th{border:1px solid #ddd;padding:6px 8px;text-align:left}</style></head><body>${inner}</body></html>`,
    );
    win.document.close();
    win.focus();
    win.print();
  };

  const idx = allArtifacts.findIndex((a) => a.id === artifact.id);
  const meta =
    allArtifacts.length > 1
      ? `${TYPE_LABEL[artifact.type] || 'Documento'} · ${idx + 1} de ${allArtifacts.length}`
      : TYPE_LABEL[artifact.type] || 'Documento';

  return (
    <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Card.Header
        className="bg-light d-flex justify-content-between align-items-start"
        style={{ gap: '8px' }}
      >
        <div style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate">{artifact.title}</div>
          <div className="text-muted small">{meta}</div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          {allArtifacts.length > 1 && (
            <Dropdown>
              <Dropdown.Toggle size="sm" variant="outline-secondary" id="artifact-selector" />
              <Dropdown.Menu align="end">
                {allArtifacts.map((a) => (
                  <Dropdown.Item
                    key={a.id}
                    active={a.id === artifact.id}
                    onClick={() => onSelect(a)}
                  >
                    <span className="d-block text-truncate" style={{ maxWidth: '240px' }}>
                      {a.title}
                    </span>
                    <span className="text-muted small">{TYPE_LABEL[a.type] || 'Documento'}</span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
          <Button variant="light" size="sm" onClick={onClose} title="Cerrar" aria-label="Cerrar">
            <FaTimes />
          </Button>
        </div>
      </Card.Header>

      <Card.Body style={{ flex: 1, overflowY: 'auto', background: '#fff', padding: 0 }}>
        <ArtifactBody artifact={artifact} tabla={tabla} />
      </Card.Body>

      <Card.Footer className="bg-light d-flex justify-content-end gap-2 flex-wrap">
        <Button variant="outline-secondary" onClick={print}>
          Imprimir / PDF
        </Button>
        <Button variant="outline-secondary" onClick={download}>
          {isTabla ? 'Descargar CSV' : 'Descargar'}
        </Button>
        <Button variant="primary" onClick={copy}>
          {copied ? (
            <>
              <FaCheck /> Copiado
            </>
          ) : (
            'Copiar'
          )}
        </Button>
      </Card.Footer>
    </Card>
  );
}

export default ArtifactPanel;
